import useReferralParams from 'hooks/useReferralParams';
import MainLayout from 'layouts/MainLayout';
import React, { FC, lazy, Suspense, useEffect, useMemo, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { openSelectStoreModal } from 'containers/MainLayout/actions';
import { Dispatch } from 'redux';
import { Redirect, Switch, withRouter, Redirect as ReactRedirect } from 'react-router-dom';

import PrivateGateLayout from 'layouts/PrivateGateLayout';
import ProductListingLayout from 'layouts/ProductListingLayout';

import GateLayout from 'layouts/GateLayout';
import AuthLayout from 'layouts/AuthLayout';

import ViewportProvider from 'components/ViewportProvider';
import {
  clearSignupReturnPath,
  getSignupSuccessPopup,
  setSignupReturnPathIfEmpty,
} from 'components/SignupRequestForm/signupRequestReturn';
import GlobalStyles from '../../global-styles';
import SelectRegionModal from './SelectRegionModal';

const SigninPopup = lazy(() => import('components/SigninPopup'));
const ForgotPasswordPopup = lazy(() => import('components/ForgotPasswordPopup'));
const SignupRequestSuccessPopupHost = lazy(() => import('components/SignupRequestForm/SignupRequestSuccessPopupHost'));
const HomePage = lazy(() => import('containers/HomePage'));
const KeepInTouch = lazy(() => import('containers/LandingPage/KeepInTouch'));
const FarmToTablePage = lazy(() => import('containers/LandingPage/FarmToTablePage'));
const KamereoBrandPage = lazy(() => import('containers/LandingPage/KamereoBrandPage'));
const ReferralPage = lazy(() => import('containers/LandingPage/ReferralPage'));
const GyomuSuper = lazy(() => import('containers/LandingPage/GyomuSuper'));
const KamereoFresh = lazy(() => import('containers/LandingPage/KamereoFresh'));
const KamereoEssential = lazy(() => import('containers/LandingPage/KamereoEssential'));
const MarketPage = lazy(() => import('containers/MarketPage'));
const RecommendedProductsPage = lazy(() => import('containers/RecommendedProductsPage'));
const BrandDetailPage = lazy(() => import('containers/BrandDetailPage'));
const DistributionLocationPage = lazy(() => import('containers/LandingPage/DistributionLocationPage'));
const DashboardPage = lazy(() => import('containers/DashboardPage'));
const AllBrandPage = lazy(() => import('containers/AllBrandPage'));
const ProductDetailsPage = lazy(() => import('containers/ProductDetailsPage'));
const OrderDetailsPage = lazy(() => import('containers/OrderDetailsPage'));
const CheckoutPage = lazy(() => import('containers/CheckoutPage'));
const OrdersPage = lazy(() => import('containers/OrdersPage'));
const QuickOrder = lazy(() => import('containers/QuickOrder'));
const FulfillmentNoteDetailPage = lazy(() => import('containers/FulfillmentNoteDetailPage'));
const PendingApprovalPage = lazy(() => import('containers/PendingApprovalPage'));
const PendingOrdersHistoryPage = lazy(() => import('containers/PendingOrdersHistoryPage'));
const FavoritePage = lazy(() => import('containers/FavoritePage'));
const AccountInfoPage = lazy(() => import('containers/AccountInfoPage'));
const CompanyInfoPage = lazy(() => import('containers/CompanyInfoPage'));
const StoreInfoPage = lazy(() => import('containers/StoreInfoPage'));
const UserManagementPage = lazy(() => import('containers/UserManagementPage'));
const UserDetailsPage = lazy(() => import('containers/UserDetailsPage'));
const GroupsPage = lazy(() => import('containers/GroupsPage'));
const GroupDetailsPage = lazy(() => import('containers/GroupDetailsPage'));
const ItemAnalysisPage = lazy(() => import('containers/ItemAnalysisPage'));
const NotificationPage = lazy(() => import('containers/NotificationPage'));
const ViewReferralInfo = lazy(() => import('containers/ViewReferralInfo'));
const NotFoundPage = lazy(() => import('containers/NotFoundPage'));
const NotAuthorizedPage = lazy(() => import('containers/NotAuthorizedPage'));
const LoginPage = lazy(() => import('containers/LoginPage'));
const SignupPage = lazy(() => import('containers/SignupPage'));
const SignupRequestPage = lazy(() => import('containers/SignupRequestPage'));
const ContactUsSuccessPage = lazy(() => import('containers/ContactUsSuccessPage'));
const ForgotPage = lazy(() => import('containers/ForgotPage'));
const ResetPage = lazy(() => import('containers/ResetPage'));
const InvitationPage = lazy(() => import('containers/InvitationPage'));
const AccountMobilePage = lazy(() => import('containers/AccountMobilePage'));
// const DocPage = lazy(() => import('containers/DocPage'));

const CONTACT_US_PATH = '/contact-us';

export const PageManager = withRouter((props: any) => {
  const prevPathnameRef = useRef(props.location.pathname);

  useEffect(() => {
    const currentPathname = props.location.pathname;
    const previousPathname = prevPathnameRef.current;

    if (currentPathname === CONTACT_US_PATH && previousPathname !== CONTACT_US_PATH) {
      setSignupReturnPathIfEmpty(previousPathname);
    } else if (previousPathname === CONTACT_US_PATH && currentPathname !== CONTACT_US_PATH) {
      clearSignupReturnPath();
    }

    prevPathnameRef.current = currentPathname;
  }, [props.location.pathname]);

  useEffect(() => {
    if (props.location.pathname !== '/thuong-hieu') {
      window.scrollTo(0, 0);
    }
    props.setRedirectPath(props.location.state?.redirectPath || null);
  }, [props.location.pathname, props.location.search, props.location.state]);

  return (
    <>
      {props.children}
      {getSignupSuccessPopup() !== null && (
        <Suspense fallback={null}>
          <SignupRequestSuccessPopupHost />
        </Suspense>
      )}
    </>
  );
});
const pathNotDisplayRegionModal = [
  '/not-found',
  '/signin',
  '/kamereo-internal-signup',
  CONTACT_US_PATH,
  '/contact-success',
  '/forgot-password',
  '/reset-password',
  '/invitations/:slug',
  '/he-thong-phan-phoi',
];

const maintainanceUrl = 'https://maintenance.prod.kamereo.vn/';

const ChangeStoreRedirectBase: FC<{ onOpenSelectStore?: () => void }> = ({ onOpenSelectStore }) => {
  useEffect(() => {
    onOpenSelectStore?.();
  }, []);

  return <ReactRedirect to="/" />;
};

const ChangeStoreRedirect = connect(null, (dispatch: Dispatch) => ({
  onOpenSelectStore: () => dispatch(openSelectStoreModal(true) as any),
}))(ChangeStoreRedirectBase);

export const App: FC = () => {
  const [redirectPath, setRedirectPath] = useState<string | null>('/signin');
  const isLogin = window.localStorage.getItem('token');
  const originalUrl = useMemo(
    () => `${window.location.pathname}${window.location.search}`,
    [window.location.pathname, window.location.search],
  );

  useReferralParams();

  const fetchWithManualRedirect = async () => {
    try {
      const response = await fetch(process.env.APP_URL || 'https://buyer.qa.kamereo.vn', {
        method: 'HEAD',
        redirect: 'manual',
        cache: 'no-store',
      });

      if (response.type === 'opaqueredirect') {
        window.location.href = `${maintainanceUrl}?redirectUrl=${encodeURIComponent(process.env.APP_URL + originalUrl)}`;
      }
    } catch (error: any) {
      console.warn('[maintenance-check] error', error?.message || error);
    }
  };

  useEffect(() => {
    if (process.env.APP_ENV === 'production') {
      const interval = setInterval(() => {
        fetchWithManualRedirect();
      }, 10000);
      return () => {
        clearInterval(interval);
      };
    }
  }, []);

  return (
    <PageManager setRedirectPath={setRedirectPath}>
      <ViewportProvider>
        <Switch>
          <ProductListingLayout path="/" exact component={HomePage} />
          <PrivateGateLayout path="/change-store" component={ChangeStoreRedirect} />
          <MainLayout
            path="/lien-he-kamereo"
            component={KeepInTouch}
            isAuth={false}
          />
          <MainLayout
            path="/tu-nong-trai-den-doanh-nghiep"
            component={FarmToTablePage}
            isAuth={false}
          />
          <MainLayout
            path="/thuong-hieu-kamereo"
            component={KamereoBrandPage}
            isAuth={false}
          />
          <MainLayout
            path="/referral-program"
            component={ReferralPage}
            isAuth={false}
          />
          <MainLayout
            path="/nguyen-lieu-nhat-ban-gyomu-japan"
            component={GyomuSuper}
            isAuth={false}
          />
          <MainLayout
            path="/kamereo-ngon"
            component={KamereoFresh}
            isAuth={false}
          />
          <MainLayout
            path="/kamereo-tien-dung"
            component={KamereoEssential}
            isAuth={false}
          />
          <ProductListingLayout path="/market" component={MarketPage} />
          <ProductListingLayout
            path="/san-pham-de-xuat"
            component={RecommendedProductsPage}
            isAuth={false}
          />
          <MainLayout
            path="/thuong-hieu/:slug"
            component={BrandDetailPage}
            isAuth={false}
          />
          <MainLayout
            path="/he-thong-phan-phoi"
            component={DistributionLocationPage}
            isAuth={false}
          />
          <MainLayout path="/overview" component={DashboardPage} isAuth={true} />
          <MainLayout path="/thuong-hieu" component={AllBrandPage} isAuth={false} />
          <MainLayout
            path="/products/:slug"
            component={ProductDetailsPage}
            isAuth={false}
          />
          <MainLayout
            path="/orders/:slug"
            component={OrderDetailsPage}
            // redirect="/not-authorized"
          />
          <MainLayout
            path={['/cart', '/checkout']}
            component={CheckoutPage}
            isAuth={false}
          />
          <MainLayout path="/orders" exact component={OrdersPage} />
          <MainLayout
            path="/quick-order"
            exact
            component={QuickOrder}
            isAuth={false}
          />
          <MainLayout
            path="/phieu-giao/:id"
            exact
            component={FulfillmentNoteDetailPage}
          />
          <MainLayout
            path="/orders-pending-approval"
            exact
            component={PendingApprovalPage}
          />
          <MainLayout
            path="/pending-orders-history"
            exact
            component={PendingOrdersHistoryPage}
          />
          <ProductListingLayout
            path="/favorite"
            component={FavoritePage}
            isAuth={true}
          />
          <MainLayout path="/account-info" component={AccountInfoPage} />
          <MainLayout path="/company-info" component={CompanyInfoPage} />
          <MainLayout path="/store-info" component={StoreInfoPage} />
          <MainLayout path="/user-management" component={UserManagementPage} />
          <MainLayout path="/user-details/:slug" component={UserDetailsPage} />
          <MainLayout path="/group-management" component={GroupsPage} />
          <MainLayout path="/groups/:slug" component={GroupDetailsPage} />
          <MainLayout path="/item-analysis" component={ItemAnalysisPage} />
          <MainLayout path="/notifications" component={NotificationPage} />
          <MainLayout path="/refer-friends" component={ViewReferralInfo} />
          <MainLayout path="/not-found" isAuth={false} component={NotFoundPage} />
          <MainLayout
            path="/not-authorized"
            isAuth={false}
            component={NotAuthorizedPage}
          />
          <AuthLayout path="/signin" component={LoginPage} />
          <GateLayout
            path="/kamereo-internal-signup"
            component={SignupPage}
            acceptGuest
          />
          <AuthLayout path={CONTACT_US_PATH} component={SignupRequestPage} />
          <GateLayout path="/contact-success" component={ContactUsSuccessPage} />
          <GateLayout path="/forgot-password" component={ForgotPage} />
          <GateLayout path="/reset-password" component={ResetPage} />
          <GateLayout
            path="/invitations/:slug"
            component={InvitationPage}
            acceptGuest
          />
          <MainLayout path="/account-mobile" component={AccountMobilePage} />
          {/* <Route
              path="/docs/:slug"
              render={(matchProps) => (
                <Suspense fallback={null}>
                  <DocPage {...matchProps} />
                </Suspense>
              )}
            /> */}
          {originalUrl.includes('/v2') && <Redirect to={originalUrl.replace('/v2', '')} />}
          <ProductListingLayout
            path="/:slug([\w-/]+)(-c[\d]{1,5})(\?lang=en)?"
            component={MarketPage}
          />
          <ProductListingLayout
            path="/:slug([A-Z0-9_]+)"
            component={MarketPage}
            sensitive
          />
          <Redirect to="/not-found" />
        </Switch>
        <GlobalStyles />
        {!pathNotDisplayRegionModal.includes(redirectPath || window.location.pathname) && !isLogin && (
          <SelectRegionModal />
        )}
        <Suspense fallback={null}>
          <SigninPopup />
          <ForgotPasswordPopup />
        </Suspense>
      </ViewportProvider>
    </PageManager>
  );
};

export default App;
